import React from "react";
import {
  Box,
  Center,
  HStack,
  Heading,
  SimpleGrid,
  Text,
  useColorMode,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Chart } from "react-google-charts";

// A breakdown pie has nothing to draw when every slice is zero. This happens
// for the Long vs Short chart whenever a user's trades have no Call/Put or
// Long/Short direction set (trade_direction is optional), and for the other
// breakdowns when there are no trades at all. Google Charts renders a blank
// donut in that case, which looks broken next to populated charts (TRADE-1008).
// `data` is the google-charts table: row 0 is the header, rows 1..n are
// [label, value] pairs.
export const hasBreakdownData = (data) =>
  Array.isArray(data) &&
  data.slice(1).some(([, value]) => Number(value) > 0);

const PIE_COLORS_4 = ["#EF6461", "#E85D75", "#60D394", "#2AB07F"];
const PIE_COLORS_8 = [
  "#EF6461",
  "#E85D75",
  "#60D394",
  "#2AB07F",
  "#5B8DEF",
  "#3A6BD5",
  "#F5B971",
  "#E8983E",
];

const ChartCard = ({ title, data, options, legend }) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "white" : "gray.700";
  const headingColor = colorMode === "light" ? "gray.600" : "gray.200";
  const legendColor = colorMode === "light" ? "gray.600" : "gray.300";
  const emptyColor = colorMode === "light" ? "gray.400" : "gray.500";

  const hasData = hasBreakdownData(data);

  return (
    <Box bg={cardBg} rounded="lg" p={4} boxShadow="sm">
      <Heading
        className="page-heading"
        fontSize="sm"
        color={headingColor}
        mb={2}
      >
        {title}
      </Heading>
      <Box h="210px">
        {hasData ? (
          <Chart chartType="PieChart" data={data} options={options} />
        ) : (
          <Center h="100%" px={4}>
            <Text fontSize="xs" color={emptyColor} textAlign="center">
              No data to display yet
            </Text>
          </Center>
        )}
      </Box>
      {hasData && (
        <Wrap spacing={2} justify="center" mt={2}>
          {legend.map(([label, color]) => (
            <WrapItem key={label}>
              <HStack spacing={1}>
                <Box w="10px" h="10px" borderRadius="sm" bg={color} />
                <Text fontSize="xs" color={legendColor}>
                  {label}
                </Text>
              </HStack>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </Box>
  );
};

const TradeBreakdownCharts = ({ stats, makePieOptions }) => {
  const optionsFour = makePieOptions(PIE_COLORS_4);
  const optionsEight = makePieOptions(PIE_COLORS_8);

  return (
    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mb={4}>
      <ChartCard
        title="Day Trade vs Swing Trade"
        data={[
          ["Trade Type", "Count"],
          ["Day L", stats.num_day_loss],
          ["Swing L", stats.num_swing_loss],
          ["Day W", stats.num_day_win],
          ["Swing W", stats.num_swing_win],
        ]}
        options={optionsFour}
        legend={[
          ["Day L", PIE_COLORS_4[0]],
          ["Swing L", PIE_COLORS_4[1]],
          ["Day W", PIE_COLORS_4[2]],
          ["Swing W", PIE_COLORS_4[3]],
        ]}
      />
      <ChartCard
        title="Options vs Shares"
        data={[
          ["Security Type", "Count"],
          ["Options L", stats.num_options_loss],
          ["Shares L", stats.num_shares_loss],
          ["Options W", stats.num_options_win],
          ["Shares W", stats.num_shares_win],
        ]}
        options={optionsFour}
        legend={[
          ["Options L", PIE_COLORS_4[0]],
          ["Shares L", PIE_COLORS_4[1]],
          ["Options W", PIE_COLORS_4[2]],
          ["Shares W", PIE_COLORS_4[3]],
        ]}
      />
      <ChartCard
        title="Long vs Short"
        data={[
          ["Trade Direction", "Count"],
          ["Long L", stats.num_long_shares_loss],
          ["Short L", stats.num_short_shares_loss],
          ["Long W", stats.num_long_shares_win],
          ["Short W", stats.num_short_shares_win],
          ["Call L", stats.num_call_options_loss],
          ["Put L", stats.num_put_options_loss],
          ["Call W", stats.num_call_options_win],
          ["Put W", stats.num_put_options_win],
        ]}
        options={optionsEight}
        legend={[
          ["Long L", PIE_COLORS_8[0]],
          ["Short L", PIE_COLORS_8[1]],
          ["Long W", PIE_COLORS_8[2]],
          ["Short W", PIE_COLORS_8[3]],
          ["Call L", PIE_COLORS_8[4]],
          ["Put L", PIE_COLORS_8[5]],
          ["Call W", PIE_COLORS_8[6]],
          ["Put W", PIE_COLORS_8[7]],
        ]}
      />
    </SimpleGrid>
  );
};

export default TradeBreakdownCharts;
